'use client';
import { useEffect, useRef, useState } from 'react';
import parse from 'html-react-parser';
import Image from 'next/image';
import { AnimateWrapper, DynamicWrapper, ImagePositionWrapper, ParallaxBannerWrapper } from '@overlapdev/wrappers';
import { Button } from '@overlapdev/elements';
import { useWindowSize } from '@overlapdev/hooks';
import { CircleArrowSVG } from 'components/svg';
import { ItemType } from '.';

const ParallaxContentItem = ({ item, last }: { item: ItemType; last: boolean }) => {
	//__Variables
	const { background_image, background_image_position, columns, column_1, column_2, content } = item || {};
	const { body_content, ctas, heading, heading_tag, subheading } = content || {};
	const { content: content_1 } = column_1 || {};
	const { content: content_2 } = column_2 || {};

	//__Hooks
	const windowSize = useWindowSize();

	//__Refs
	const contentRef = useRef<HTMLDivElement | null>(null);

	//__State
	const [height, setHeight] = useState<number>(0);
	const [marginBottom, setMarginBottom] = useState<number>(0);
	const [contentTop, setContentTop] = useState<number>(0);

	//__Use Effect
	useEffect(() => {
		if (contentRef?.current && windowSize[1]) {
			const content_height = contentRef.current.offsetHeight;
			setHeight(last ? content_height + windowSize[1] * 2 - content_height / 1.5 : content_height / 4 + windowSize[1] * 3);
			setMarginBottom(last ? 0 : content_height / 4 + windowSize[1] * 2);
			setContentTop(content_height / 2);
		}
	}, [windowSize]);

	const inlineStyles = {
		item: {
			height: height ? `${height}px` : last ? '200vh' : '300vh',
			marginBottom: marginBottom ? `-${marginBottom}px` : last ? '0px' : '-100vh'
		},
		content: {
			transform: contentTop ? `translateY(-${contentTop}px)` : ''
		}
	};

	if (!item) return null;
	return (
		<div className={`relative`} style={inlineStyles.item}>
			<div className='sticky top-0'>
				<div className='h-[100vh] relative w-full'>
					{background_image?.url ? (
						<div className='bg-black h-full absolute overflow-hidden w-full'>
							<ParallaxBannerWrapper
								className='h-full parallax_banner'
								layers={[
									{
										children: (
											<ImagePositionWrapper className='h-full' position={background_image_position}>
												<Image src={background_image.url} alt={background_image.alt} fill className='object-cover opacity-60' />
											</ImagePositionWrapper>
										),
										easing: [0, 0, 1, 1],
										shouldAlwaysCompleteAnimation: true,
										speed: -15,
										translateY: ['130px', '-130px']
									}
								]}
							/>
						</div>
					) : null}
				</div>
			</div>
			<div className='gutter-x relative'>
				<div className='flex flex-col gap-5 inner text-white md:gap-30' ref={contentRef} style={inlineStyles.content}>
					<AnimateWrapper element='div' animate_type='fadeup'>
						{subheading ? <h6 className='mb-5'>{parse(subheading)}</h6> : null}
						{heading ? (
							<DynamicWrapper wrapper={heading_tag}>
								<span>{parse(heading)}</span>
							</DynamicWrapper>
						) : null}
					</AnimateWrapper>
					{columns ? (
						<div className='flex gap-4 max-md:flex-col max-md:gap-10 lg:gap-24'>
							<div className='flex flex-col gap-10 w-full'>
								{content_1?.subheading ? (
									<AnimateWrapper element='h6' animate_type='fadeup'>
										{parse(content_1.subheading)}
									</AnimateWrapper>
								) : null}
								{content_1?.heading ? (
									<AnimateWrapper element={content_1.heading_tag} animate_type='fadeup'>
										{parse(content_1?.heading)}
									</AnimateWrapper>
								) : null}
								{content_1?.body_content ? (
									<AnimateWrapper element='div' animate_type='fadeup' className='font-semibold text-lg lg:text-2xl'>
										{parse(content_1.body_content)}
									</AnimateWrapper>
								) : null}
								{content_1?.ctas ? (
									<AnimateWrapper element='div' animate_type='fadeup'>
										{content_1.ctas.map((cta, index) =>
											cta.link?.url ? (
												<Button key={`parallax_content_1_cta-${index}`} link={cta.link} className={cta.style || 'btn'}>
													<CircleArrowSVG width={18} />
												</Button>
											) : null
										)}
									</AnimateWrapper>
								) : null}
							</div>
							<div className='flex flex-col gap-10 w-full'>
								{content_2?.subheading ? (
									<AnimateWrapper element='h6' animate_type='fadeup'>
										{parse(content_2.subheading)}
									</AnimateWrapper>
								) : null}
								{content_2?.heading ? (
									<AnimateWrapper element={content_2.heading_tag} animate_type='fadeup'>
										{parse(content_2?.heading)}
									</AnimateWrapper>
								) : null}
								{content_2?.body_content ? (
									<AnimateWrapper element='div' animate_type='fadeup' className='font-semibold text-lg lg:text-2xl' threshold={0}>
										{parse(content_2.body_content)}
									</AnimateWrapper>
								) : null}
								{content_2?.bottom_content ? (
									<AnimateWrapper element='div' animate_type='fadeup' className='bg-dark-grey font-semibold p-8 rounded-3xl lg:p-10'>
										{parse(content_2.bottom_content)}
									</AnimateWrapper>
								) : null}
								{content_2?.ctas ? (
									<div>
										{content_2.ctas.map((cta, index) =>
											cta.link?.url ? (
												<Button key={`parallax_content_2_cta-${index}`} link={cta.link} className={cta.style || 'btn'}>
													<CircleArrowSVG width={18} />
												</Button>
											) : null
										)}
									</div>
								) : null}
							</div>
						</div>
					) : (
						<div className='max-w-[700px]'>
							{body_content ? (
								<AnimateWrapper element='div' animate_type='fadeup' className='font-semibold text-lg'>
									{parse(body_content)}
								</AnimateWrapper>
							) : null}
							{ctas?.length ? (
								<AnimateWrapper element='div' animate_type='fadeup' className='flex gap-6 mt-10'>
									{ctas.map((cta, index) =>
										cta.link?.url ? (
											<Button key={`parallax_content_cta-${index}`} link={cta.link} className={cta.style || 'arrow_link'}>
												<CircleArrowSVG width={18} />
											</Button>
										) : null
									)}
								</AnimateWrapper>
							) : null}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default ParallaxContentItem;
